import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import axiosInstance from '../../api/axiosConfig';
import PageContainer from '../common/PageContainer';
import PageHeader from '../common/PageHeader';

const AiLessonPlanner = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [form, setForm] = useState({
    subject: '',
    grade: '',
    topic: '',
    durationMinutes: 45,
    objectives: '',
    teachingStyle: 'interactive',
  });

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const generate = async () => {
    setError('');
    setResult('');
    if (!form.subject || !form.grade || !form.topic) {
      setError('Subject, grade, and topic are required.');
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.post('/api/ai-assistant/lesson-plan', form);
      setResult(response?.data?.content || 'No lesson plan generated.');
    } catch (e) {
      setError('Failed to generate lesson plan.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader title="AI Lesson Planner" subtitle="Generate teacher-ready lesson plans by class, subject, and topic." />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Subject" size="small" value={form.subject} onChange={(e) => update('subject', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Grade" size="small" value={form.grade} onChange={(e) => update('grade', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Topic" size="small" value={form.topic} onChange={(e) => update('topic', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Duration (minutes)" size="small" type="number" value={form.durationMinutes} onChange={(e) => update('durationMinutes', Number(e.target.value || 45))} />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Teaching Style" size="small" value={form.teachingStyle} onChange={(e) => update('teachingStyle', e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Learning Objectives (optional)"
              value={form.objectives}
              onChange={(e) => update('objectives', e.target.value)}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={generate} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Lesson Plan'}
          </Button>
        </Box>

        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

        {result ? (
          <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Generated Plan</Typography>
            <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0, fontFamily: 'inherit', fontSize: '0.9rem' }}>
              {result}
            </Typography>
          </Paper>
        ) : null}
      </Paper>
    </PageContainer>
  );
};

export default AiLessonPlanner;
